import { type ModelMessage, modelMessageSchema } from "ai";
import { SUPPORTED_MODELS } from "./supportedModels";

export const CONTEXT_COMPACTION_SUMMARY_MODEL =
	"google/gemini-3.7-flash" as const;
const SOL_MODEL = "openai/gpt-5.6-sol" as const;
export const CONTEXT_COMPACTION_REQUIRED = "CONTEXT_COMPACTION_REQUIRED";
export const CONTEXT_SUMMARY_TERMINAL_MARKER =
	"<!-- CONTEXT_SUMMARY_COMPLETE -->";
const UNKNOWN_CONTEXT_WINDOW_TOKENS = 32_768;
const CONTEXT_SUMMARY_TOKEN_RESERVE = 8_000;
const COMPACTED_CONTEXT_PREFIX = "<compacted_conversation_context>\n";
const COMPACTED_CONTEXT_SUFFIX = "\n</compacted_conversation_context>";

function estimateCharactersAsTokens(length: number): number {
	return Math.max(1, Math.ceil(length / 4));
}

export function estimateSerializedTokens(serialized: string): number {
	return estimateCharactersAsTokens(serialized.length);
}

export type CompactedContextExtras = {
	fetchedArtifacts?: readonly string[];
	activeToolBundles?: readonly string[];
};

function fetchedArtifactsSection(artifacts: readonly string[]): string {
	return (
		"<fetched_artifacts>\n" +
		"Already fetched earlier in this conversation; key findings are in the summary above. " +
		"Do not fetch these again unless the summary lacks a detail you need:\n" +
		artifacts.map((artifact) => `- ${artifact}`).join("\n") +
		"\n</fetched_artifacts>"
	);
}

function activeToolBundlesSection(bundles: readonly string[]): string {
	return (
		"<active_tool_bundles>\n" +
		`Your discoverTools selection is still active: ${bundles.join(", ")}. ` +
		"These bundles' tools are available now — do not call discoverTools to re-select them; " +
		"call it only to change the set.\n" +
		"</active_tool_bundles>"
	);
}

export function compactedContextBlock(
	summary: string,
	extras?: CompactedContextExtras,
): string {
	const sections = [summary];
	if (extras?.fetchedArtifacts?.length) {
		sections.push(fetchedArtifactsSection(extras.fetchedArtifacts));
	}
	if (extras?.activeToolBundles?.length) {
		sections.push(activeToolBundlesSection(extras.activeToolBundles));
	}
	return (
		COMPACTED_CONTEXT_PREFIX + sections.join("\n\n") + COMPACTED_CONTEXT_SUFFIX
	);
}

export function postCompactionPromptProjection(args: {
	providerPromptTokens: number;
	estimatedRemovalTokens: number;
	summary: string;
	preservedUserMessage?: string;
	compactAtTokens: number;
}): { projectedPromptTokens: number; safeToResume: boolean } {
	const replacementTokens =
		estimateCharactersAsTokens(
			COMPACTED_CONTEXT_PREFIX.length +
				args.summary.length +
				COMPACTED_CONTEXT_SUFFIX.length,
		) +
		(args.preservedUserMessage
			? estimateSerializedTokens(args.preservedUserMessage)
			: 0);
	const projectedPromptTokens =
		Math.max(0, args.providerPromptTokens - args.estimatedRemovalTokens) +
		replacementTokens;
	return {
		projectedPromptTokens,
		safeToResume: projectedPromptTokens < args.compactAtTokens,
	};
}

export type ContextPolicy = {
	contextWindowTokens: number;
	priceTierStartsAtTokens?: number;
	compactAtTokens: number;
	targetPromptTokens: number;
};

export type ContextHistoryMessage = {
	order: number;
	stepOrder?: number;
	role: string;
	serialized: string;
	estimatedTokens?: number;
};

type SkipPlan = {
	decision: "skip";
	reason:
		| "below_threshold"
		| "missing_provider_usage"
		| "insufficient_history"
		| "no_new_history";
};

type CompactPlan = {
	decision: "compact";
	policy: ContextPolicy;
	triggerPromptTokens: number;
	requiredRemovalTokens: number;
	compactedThroughOrder: number;
	compactedThroughStepOrder: number;
	retainedRecentMessages: number;
	estimatedRemovalTokens: number;
	reachesTarget: boolean;
	preservedUserMessage?: string;
	messagesToSummarize: ContextHistoryMessage[];
};

export type ContextCompactionPlan = SkipPlan | CompactPlan;

export function completedContextSummary(args: {
	text: string;
	finishReason: string;
}): string | null {
	if (args.finishReason !== "stop") return null;
	const text = args.text.trim();
	if (!text.endsWith(CONTEXT_SUMMARY_TERMINAL_MARKER)) return null;
	const summary = text.slice(0, -CONTEXT_SUMMARY_TERMINAL_MARKER.length).trim();
	return summary || null;
}

export function parsePreservedUserMessage(
	serialized: string | undefined,
): ModelMessage | undefined {
	if (!serialized) return undefined;
	let parsed: unknown;
	try {
		parsed = JSON.parse(serialized);
	} catch {
		return undefined;
	}
	const result = modelMessageSchema.safeParse(parsed);
	if (!result.success || result.data.role !== "user") return undefined;
	return result.data;
}

function baseModelId(modelId: string): string {
	return modelId.endsWith(":online")
		? modelId.slice(0, -":online".length)
		: modelId;
}

export function contextCompactionSummaryModel(args: {
	modelId: string;
	mandatoryModel: typeof CONTEXT_COMPACTION_SUMMARY_MODEL | null;
}): typeof SOL_MODEL | typeof CONTEXT_COMPACTION_SUMMARY_MODEL {
	if (args.mandatoryModel !== null) return args.mandatoryModel;
	return baseModelId(args.modelId) === SOL_MODEL
		? SOL_MODEL
		: CONTEXT_COMPACTION_SUMMARY_MODEL;
}

export function promptMeasurementKeyForMessage(args: {
	modelId: string;
	order: number;
	stepOrder?: number;
}): string {
	return `message:${baseModelId(args.modelId)}:${args.order}:${args.stepOrder ?? 0}`;
}

function compactAtTokens(model: {
	contextWindowTokens: number;
	priceTierStartsAtTokens?: number;
	compactAtTokens?: number;
}): number {
	if (model.compactAtTokens !== undefined) return model.compactAtTokens;
	if (model.priceTierStartsAtTokens === 200_000) return 170_000;
	const ratio = model.contextWindowTokens <= 262_144 ? 0.7 : 0.5;
	return Math.floor(model.contextWindowTokens * ratio);
}

function targetPromptTokens(model: {
	contextWindowTokens: number;
	priceTierStartsAtTokens?: number;
	compactAtTokens?: number;
	targetPromptTokens?: number;
}): number {
	if (model.targetPromptTokens !== undefined) return model.targetPromptTokens;
	if (model.priceTierStartsAtTokens === 200_000) return 120_000;
	return Math.floor(compactAtTokens(model) * 0.7);
}

export function getContextPolicy(modelId: string): ContextPolicy {
	const baseId = baseModelId(modelId);
	const model = SUPPORTED_MODELS.find((candidate) => candidate.id === baseId);
	if (!model) {
		return {
			contextWindowTokens: UNKNOWN_CONTEXT_WINDOW_TOKENS,
			compactAtTokens: Math.floor(UNKNOWN_CONTEXT_WINDOW_TOKENS * 0.7),
			targetPromptTokens: Math.floor(UNKNOWN_CONTEXT_WINDOW_TOKENS * 0.49),
		};
	}
	return {
		contextWindowTokens: model.contextWindowTokens,
		...(model.priceTierStartsAtTokens
			? { priceTierStartsAtTokens: model.priceTierStartsAtTokens }
			: {}),
		compactAtTokens: compactAtTokens(model),
		targetPromptTokens: targetPromptTokens(model),
	};
}

export function mandatoryContextCompactionModel(args: {
	modelId: string;
	providerPromptTokens: number | undefined;
	promptMeasurementKey?: string;
	summarizedMeasurementKey?: string;
}): typeof CONTEXT_COMPACTION_SUMMARY_MODEL | null {
	if (args.providerPromptTokens === undefined) return null;
	if (
		args.promptMeasurementKey !== undefined &&
		args.promptMeasurementKey === args.summarizedMeasurementKey
	) {
		return null;
	}
	return args.providerPromptTokens >=
		getContextPolicy(args.modelId).contextWindowTokens
		? CONTEXT_COMPACTION_SUMMARY_MODEL
		: null;
}

export function assertRequiredContextCompaction(
	required: boolean,
	plan?: ContextCompactionPlan,
): void {
	if (
		(plan?.decision === "compact" && plan.estimatedRemovalTokens <= 0) ||
		(required && plan?.decision !== "compact")
	) {
		throw new Error(CONTEXT_COMPACTION_REQUIRED);
	}
}

export function shouldLoadCompactionHistory(args: {
	modelId: string;
	providerPromptTokens: number | undefined;
	hasActiveSummary: boolean;
}): boolean {
	if (args.hasActiveSummary) return true;
	if (args.providerPromptTokens === undefined) return false;
	return (
		args.providerPromptTokens >= getContextPolicy(args.modelId).compactAtTokens
	);
}

function isRecord(value: unknown): value is Record<string, unknown> {
	return typeof value === "object" && value !== null;
}

function toolIds(message: ContextHistoryMessage): {
	calls: string[];
	results: string[];
} {
	let parsed: unknown;
	try {
		parsed = JSON.parse(message.serialized);
	} catch {
		return { calls: [], results: [] };
	}
	if (!isRecord(parsed) || !Array.isArray(parsed.content)) {
		return { calls: [], results: [] };
	}
	const calls: string[] = [];
	const results: string[] = [];
	for (const part of parsed.content) {
		if (!isRecord(part) || typeof part.toolCallId !== "string") continue;
		if (part.type === "tool-call") calls.push(part.toolCallId);
		if (part.type === "tool-result") results.push(part.toolCallId);
	}
	return { calls, results };
}

/** Read-only fetch tools worth an anti-re-fetch digest, with their id arg. */
const FETCHED_ARTIFACT_ID_PATHS: Record<
	string,
	readonly (readonly string[])[]
> = {
	readFile: [
		["source", "fileId"],
		["source", "fileRef"],
		["source", "url"],
	],
	loadSkill: [["slug"]],
	inspectWorkflowSop: [["sopFileId"]],
	codexRead: [["key"]],
	listOneDriveFolder: [["folderId"]],
	cacheOneDriveFile: [["fileId"]],
};
const FETCHED_ARTIFACT_ID_MAX_CHARS = 120;
export const FETCHED_ARTIFACTS_MAX = 60;

function artifactId(input: unknown, paths: readonly (readonly string[])[]) {
	for (const path of paths) {
		const value = path.reduce<unknown>(
			(acc, key) =>
				isRecord(acc) && Object.hasOwn(acc, key) ? acc[key] : undefined,
			input,
		);
		if (typeof value === "string" && value.length > 0) {
			return `${path[path.length - 1]}=${value.slice(0, FETCHED_ARTIFACT_ID_MAX_CHARS)}`;
		}
	}
	return undefined;
}

export function extractFetchedArtifacts(
	messages: readonly ContextHistoryMessage[],
): string[] {
	const digests = new Set<string>();
	for (const message of messages) {
		let parsed: unknown;
		try {
			parsed = JSON.parse(message.serialized);
		} catch {
			continue;
		}
		if (!isRecord(parsed) || !Array.isArray(parsed.content)) continue;
		for (const part of parsed.content) {
			if (!isRecord(part) || part.type !== "tool-call") continue;
			if (typeof part.toolName !== "string") continue;
			const paths = FETCHED_ARTIFACT_ID_PATHS[part.toolName];
			if (!paths) continue;
			const id = artifactId(part.input ?? part.args, paths);
			if (id) digests.add(`${part.toolName} ${id}`);
		}
	}
	return [...digests];
}

/** Rolling union across compactions; newest kept, oldest dropped past cap. */
export function mergeFetchedArtifacts(
	prior: readonly string[] | undefined,
	next: readonly string[],
): string[] {
	const merged = [...new Set([...(prior ?? []), ...next])];
	return merged.slice(-FETCHED_ARTIFACTS_MAX);
}

export function isMessageAfterCompactionBoundary(
	message: ContextHistoryMessage,
	order: number | undefined,
	stepOrder: number | undefined,
): boolean {
	if (order === undefined) return true;
	if (message.order !== order) return message.order > order;
	if (stepOrder === undefined) return false;
	return (message.stepOrder ?? 0) > stepOrder;
}

function messageTokenEstimate(message: ContextHistoryMessage): number {
	return (
		message.estimatedTokens ?? estimateSerializedTokens(message.serialized)
	);
}

export function planContextCompaction(args: {
	modelId: string;
	providerPromptTokens: number | undefined;
	messages: ContextHistoryMessage[];
	compactedThroughOrder?: number;
	compactedThroughStepOrder?: number;
}): ContextCompactionPlan {
	if (args.providerPromptTokens === undefined) {
		return { decision: "skip", reason: "missing_provider_usage" };
	}
	const policy = getContextPolicy(args.modelId);
	if (args.providerPromptTokens < policy.compactAtTokens) {
		return { decision: "skip", reason: "below_threshold" };
	}
	if (args.messages.length < 2) {
		return { decision: "skip", reason: "insufficient_history" };
	}
	const firstNewIndex = args.messages.findIndex((message) =>
		isMessageAfterCompactionBoundary(
			message,
			args.compactedThroughOrder,
			args.compactedThroughStepOrder,
		),
	);
	if (firstNewIndex === -1) {
		return { decision: "skip", reason: "no_new_history" };
	}

	const results = new Set<string>();
	const calls: Array<{ id: string; index: number }> = [];
	for (const [index, message] of args.messages.entries()) {
		const ids = toolIds(message);
		for (const id of ids.calls) calls.push({ id, index });
		for (const id of ids.results) results.add(id);
	}
	const unresolvedStart = calls.reduce(
		(start, call) =>
			results.has(call.id) || call.index >= start ? start : call.index,
		args.messages.length,
	);
	const maxCandidateIndex = unresolvedStart - 1;
	if (maxCandidateIndex < firstNewIndex) {
		return { decision: "skip", reason: "insufficient_history" };
	}

	const requiredRemoval =
		args.providerPromptTokens -
		policy.targetPromptTokens +
		CONTEXT_SUMMARY_TOKEN_RESERVE;
	let latestUserIndex = -1;
	for (let index = args.messages.length - 1; index >= 0; index--) {
		if (args.messages[index]?.role !== "user") continue;
		latestUserIndex = index;
		break;
	}
	const latestUserMessage =
		latestUserIndex >= firstNewIndex
			? args.messages[latestUserIndex]
			: undefined;
	const pending = new Set<string>();
	let estimatedRemoval = 0;
	let selectedIndex: number | undefined;
	for (let index = firstNewIndex; index <= maxCandidateIndex; index++) {
		const message = args.messages[index];
		if (!message) continue;
		const ids = toolIds(message);
		for (const id of ids.calls) pending.add(id);
		for (const id of ids.results) pending.delete(id);
		estimatedRemoval += messageTokenEstimate(message);
		if (pending.size !== 0) continue;
		selectedIndex = index;
		const preservedUserTokens =
			latestUserMessage && latestUserIndex <= index
				? messageTokenEstimate(latestUserMessage)
				: 0;
		if (estimatedRemoval - preservedUserTokens >= requiredRemoval) break;
	}
	if (selectedIndex === undefined) {
		return { decision: "skip", reason: "insufficient_history" };
	}

	const boundary = args.messages[selectedIndex];
	if (!boundary) return { decision: "skip", reason: "insufficient_history" };
	const preservedUserTokens =
		latestUserMessage && latestUserIndex <= selectedIndex
			? messageTokenEstimate(latestUserMessage)
			: 0;
	const effectiveRemoval = estimatedRemoval - preservedUserTokens;
	const messagesToSummarize = args.messages.slice(
		firstNewIndex,
		selectedIndex + 1,
	);
	return {
		decision: "compact",
		policy,
		triggerPromptTokens: args.providerPromptTokens,
		requiredRemovalTokens: requiredRemoval,
		compactedThroughOrder: boundary.order,
		compactedThroughStepOrder: boundary.stepOrder ?? 0,
		estimatedRemovalTokens: effectiveRemoval,
		reachesTarget: effectiveRemoval >= requiredRemoval,
		retainedRecentMessages: args.messages.length - selectedIndex - 1,
		...(latestUserIndex >= firstNewIndex && latestUserIndex <= selectedIndex
			? { preservedUserMessage: args.messages[latestUserIndex]?.serialized }
			: {}),
		messagesToSummarize,
	};
}
